import React, { useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import DamagePartPicker from "../../../Components/DamagePartPicker";
import { RULES } from "../../../Helpers/bookingRules";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";

// Phase A, Step 1 - its own modal (split out of the original combined
// wizard per the user's request). Mirrors the previous (legacy)
// admin-backend's own /trip/start/images/:bookingId: a dedicated call that
// uploads to Cloudinary AND writes tripDetails.startImage/startDamages onto
// the booking immediately (still Booked), merged with whatever's already
// there - not "hold URLs in local state until Step 2".
//
// Once this modal's "Done" completes, the parent (Pages/Booking/View/index.js)
// closes it and reloads the booking - since tripDetails.startImage is now
// populated, the page swaps its "Upload Start Images" button for "Start
// Trip", which opens the separate StartTripModal (Step 2).
//
// initialImages/initialDamages (from the booking's own tripDetails, passed
// down by the parent) seed this modal's state so reopening it after a
// partial upload shows what's already there instead of starting empty.
//
// Modal markup follows this codebase's own existing convention (plain
// state-controlled `.modal.fade.show` + a separate backdrop div - see
// Pages/VehicleFleet/Car/View.js), not react-bootstrap.

const MIN_IMAGES = RULES.TRIP_IMAGES_MIN;
const MAX_IMAGES = RULES.TRIP_IMAGES_MAX;

const UploadStartImagesModal = ({ bookingId, initialImages, initialDamages, onClose, onUploaded }) => {
    const [imageTemp, setImageTemp] = useState([]); // [{imgId, file, url}] - selected, not yet uploaded
    const [uploadedUrls, setUploadedUrls] = useState(() => initialImages || []); // already persisted on the booking
    const [uploading, setUploading] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [damages, setDamages] = useState(() => initialDamages || []);

    const busy = uploading || finishing;
    const totalSelected = imageTemp.length + uploadedUrls.length;

    const imageChange = (e) => {
        const files = Object.values(e.target.files || {});
        if (!files.length) return;
        let room = MAX_IMAGES - imageTemp.length - uploadedUrls.length;
        if (files.length > room) toast.error(`Only ${MAX_IMAGES} photos are allowed; ${Math.max(room, 0)} more can be added`);
        files.forEach((file) => {
            if (room <= 0) return;
            if (!/^image\//.test(file.type)) { toast.error(`${file.name} is not an image`); return; }
            if (file.size <= RULES.IMAGE_MAX_BYTES) {
                room -= 1;
                setImageTemp((prev) => [...prev, { imgId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) }]);
            } else {
                toast.error("Image must be below 10 MB");
            }
        });
        e.target.value = "";
    };

    const removeImage = (imgId) => setImageTemp((prev) => prev.filter((v) => v.imgId !== imgId));
    const removeUploadedUrl = (url) => setUploadedUrls((prev) => prev.filter((u) => u !== url));

    // Field name (startImages) and route (/trip/start/images/:bookingId)
    // match the previous admin-backend's own upload API. This call persists
    // straight onto the booking - the response's startImage is the full
    // merged list (previous + this batch), not just this batch. Called with
    // zero files it still syncs the current startDamages list (the backend
    // keeps the existing images and just replaces startDamages), which is
    // how "Done" makes sure damage notes added after the last upload aren't
    // lost.
    const saveToBooking = async (files) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("startImages", f));
        formData.append("startDamages", JSON.stringify(damages));
        formData.append("keepImages", JSON.stringify(uploadedUrls));
        const response = await Handler({ method: "post", url: `/admin/booking/trip/start/images/${bookingId}`, data: formData, formdata: true });
        if (response?.success && response?.data?.data?.startImage) {
            setUploadedUrls(response.data.data.startImage);
            return true;
        }
        toast.error(response?.data?.message || "Some images failed to upload");
        return false;
    };

    const uploadImages = async () => {
        if (!imageTemp.length) {
            toast.error("Select at least 1 image");
            return;
        }
        setUploading(true);
        const ok = await saveToBooking(imageTemp.map((v) => v.file));
        setUploading(false);
        if (ok) {
            setImageTemp([]);
            toast.success("Images uploaded");
        }
    };

    const finish = async () => {
        if (totalSelected < MIN_IMAGES) {
            toast.error(`Add at least ${MIN_IMAGES} start photos (${totalSelected} so far)`);
            return;
        }
        setFinishing(true);
        // Flush any still-selected files plus the current damages list in
        // one final call, so clicking "Done" right after picking files (or
        // after only adding a damage note) doesn't lose anything.
        const ok = await saveToBooking(imageTemp.map((v) => v.file));
        setFinishing(false);
        if (ok) {
            setImageTemp([]);
            toast.success("Start images saved");
            onUploaded?.();
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Upload Start Images</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={busy}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-2 small text-muted">
                                Upload up to {MAX_IMAGES} photos of the car at handover. At least {MIN_IMAGES} photos are required before the trip can be started ({totalSelected} added).
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {uploadedUrls.map((url) => (
                                    <div key={url} className="position-relative">
                                        <img src={url} alt="" width={84} height={84} className="rounded-3 border border-success" style={{ objectFit: "cover" }} />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger p-0 rounded-circle position-absolute"
                                            style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }}
                                            onClick={() => removeUploadedUrl(url)}
                                            title="Remove"
                                        >
                                            <ImCross size={8} />
                                        </button>
                                    </div>
                                ))}
                                {imageTemp.map((v) => (
                                    <div key={v.imgId} className="position-relative">
                                        <img src={v.url} alt="" width={84} height={84} className="rounded-3" style={{ objectFit: "cover", opacity: 0.7 }} />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger p-0 rounded-circle position-absolute"
                                            style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }}
                                            onClick={() => removeImage(v.imgId)}
                                            title="Remove"
                                        >
                                            <ImCross size={8} />
                                        </button>
                                    </div>
                                ))}
                                {totalSelected < MAX_IMAGES && (
                                    <label
                                        className="d-flex align-items-center justify-content-center rounded-3 border text-muted"
                                        style={{ width: 84, height: 84, cursor: busy ? "not-allowed" : "pointer" }}
                                    >
                                        <FaCloudUploadAlt size={20} />
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/png,image/jpeg,image/webp"
                                            className="d-none"
                                            onChange={imageChange}
                                            disabled={busy}
                                        />
                                    </label>
                                )}
                            </div>
                            
                            {/* {!!imageTemp.length && (
                                <button type="button" className="btn btn-sm btn-outline-primary mb-3" disabled={busy} onClick={uploadImages}>
                                    {uploading ? "Uploading..." : `Upload ${imageTemp.length} photo${imageTemp.length > 1 ? "s" : ""}`}
                                </button>
                            )} */}

                            <hr />
                            <div className="fw-semibold small mb-2">Damage noted at handover (optional)</div>
                            <DamagePartPicker damages={damages} setDamages={setDamages} disabled={busy} />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-success" onClick={finish} disabled={busy || totalSelected < MIN_IMAGES}>
                                {finishing ? "Saving..." : "Done"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default UploadStartImagesModal;
